// Helper Utility Functions

export const classNames = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const throttle = <T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

/**
 * Capitalizes the first letter of a string
 * @param text - The string to capitalize
 * @returns The string with the first letter capitalized
 * @example capitalize('hello world') // 'Hello world'
 */
export const capitalize = (text: string): string => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Capitalizes the first letter of each word in a string
 * @param text - The string to capitalize
 * @returns The string with each word capitalized
 * @example capitalizeWords('hello world') // 'Hello World'
 */
export const capitalizeWords = (text: string): string => {
  if (!text) return text;
  return text
    .split(' ')
    .map((word) => word.split('.').map(capitalize).join('.'))
    .join(' ');
};

/**
 * Capitalizes the all letter of each word
 * @param text - The string to capitalize
 * @returns The string with each word capitalized
 * @example capitalizeAllWords('hello world') // 'HELLO WORLD'
 */
export const capitalizeAllWords = (text: string): string => {
  if (!text) return text;
  return text.toUpperCase();
};

/**
 * Alias for capitalizeWords - capitalizes the first letter of each word
 * @param text - The string to capitalize
 * @returns The string with each word capitalized
 */
export const capitalizeEachWord = capitalizeWords;

// --- Colors ---

const BRIGHT_COLORS = ['#FF5722', '#2196F3', '#4CAF50', '#FFC107'];

/**
 * Gets a stable "random" color from BRIGHT_COLORS array based on a string ID
 */
export const getStableColor = (id: string = ''): string => {
  if (!id) return BRIGHT_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % BRIGHT_COLORS.length;
  return BRIGHT_COLORS[index];
};

/**
 * Determines if a color is dark based on its YIQ brightness.
 * Useful for deciding whether to use white or black text on top of the color.
 * @param color - Hex color string (e.g. '#FFFFFF' or '#000')
 * @returns true if the color is dark, false otherwise
 */
export const isDarkColor = (color: string): boolean => {
  if (!color) return true;
  let hex = color.replace('#', '');

  // Handle shorthand hex colors (e.g. #000)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  if (hex.length < 6) return true;

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // YIQ formula for perceived brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 128;
};

/**
 * Generates a consistent hex color from a string
 */
export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

/**
 * Generates a random vibrant hex color
 */
export const getRandomColor = () => {
  const h = Math.random();
  const s = 0.6 + Math.random() * 0.2; // 60-80%
  const l = 0.5 + Math.random() * 0.1; // 50-60%

  const hslToHex = (h: number, s: number, l: number) => {
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h * 12) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  return hslToHex(h, s, l);
};
